import type { AuthBackend, PortalAuthConfig } from './auth.js';
import { readClaims } from './claims.js';
import { whenIdle } from './routes.js';

/**
 * The sign-in library (Amplify, about 35 KB gzip) off the first load (plan 45
 * phase 2): a signed-in person's first page renders from the access token the
 * library stored last time, read straight from storage while it is valid for
 * at least another minute. The library loads when the browser is idle after
 * that, or at once for anything else (sign-in, a refresh, MFA, sign-out). The
 * API still verifies every token (hard rule 1); nothing here is trusted.
 */

/** Seconds of validity a stored token must still have to be used without the library. */
export const STORED_TOKEN_MARGIN_S = 60;

type Store = Pick<Storage, 'getItem'>;

/** The access token Amplify stored for this app client, if it is still valid. */
export function storedAccessToken(
  clientId: string,
  store: Store | undefined,
  nowS: number = Math.floor(Date.now() / 1000),
): string | undefined {
  if (!store) return undefined;
  try {
    const prefix = `CognitoIdentityServiceProvider.${clientId}`;
    const user = store.getItem(`${prefix}.LastAuthUser`);
    if (!user) return undefined;
    const token = store.getItem(`${prefix}.${user}.accessToken`);
    if (!token) return undefined;
    const drift = Number(store.getItem(`${prefix}.${user}.clockDrift`) ?? 0) || 0;
    const { expires } = readClaims(token);
    // Amplify's clock drift is in ms (server minus device); compare in server time.
    const serverNow = nowS + Math.round(drift / 1000);
    return expires - serverNow > STORED_TOKEN_MARGIN_S ? token : undefined;
  } catch {
    return undefined;
  }
}

function storageFor(kind: PortalAuthConfig['storage']): Store | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return undefined;
  }
}

export function lazyAuth(
  config: PortalAuthConfig,
  {
    load = () => import('./auth.js').then((m) => m.amplifyAuth(config)),
    store = storageFor(config.storage),
    // Well after the first page has settled (10 s, then an idle slot): the library's
    // script never competes with the page the person opened.
    idle = (task: () => void) => void setTimeout(() => whenIdle(task, 3000), 10_000),
  }: {
    load?: () => Promise<AuthBackend>;
    store?: Store | undefined;
    idle?: (task: () => void, timeout?: number) => void;
  } = {},
): AuthBackend {
  let real: Promise<AuthBackend> | undefined;
  const backend = () => {
    real ??= load().catch((error: unknown) => {
      real = undefined;
      throw error;
    });
    return real;
  };
  let warmed = false;
  const warm = () => {
    if (warmed) return;
    warmed = true;
    idle(() => void backend().catch(() => undefined));
  };
  const call =
    <A extends unknown[], R>(pick: (b: AuthBackend) => ((...args: A) => Promise<R>) | undefined) =>
    async (...args: A): Promise<R> => {
      const fn = pick(await backend());
      if (!fn) throw new Error('Not supported by this sign-in backend');
      return fn(...args);
    };
  return {
    async accessToken(forceRefresh = false) {
      if (!forceRefresh) {
        const stored = storedAccessToken(config.clientId, store);
        if (stored) {
          warm();
          return stored;
        }
      }
      return (await backend()).accessToken(forceRefresh);
    },
    signIn: call((b) => b.signIn.bind(b)),
    confirm: call((b) => b.confirm.bind(b)),
    signOut: call((b) => b.signOut.bind(b)),
    startTotpSetup: call((b) => b.startTotpSetup.bind(b)),
    finishTotpSetup: call((b) => b.finishTotpSetup.bind(b)),
    changePassword: call((b) => b.changePassword.bind(b)),
    forgotPassword: call((b) => b.forgotPassword.bind(b)),
    confirmForgotPassword: call((b) => b.confirmForgotPassword.bind(b)),
    useEmailCode: call((b) => b.useEmailCode?.bind(b)),
    enableEmailMfa: call((b) => b.enableEmailMfa?.bind(b)),
    allowEmailFallback: call((b) => b.allowEmailFallback?.bind(b)),
    federatedSignIn: call((b) => b.federatedSignIn?.bind(b)),
  };
}
